import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { MultiplierPanel } from "./MultiplierPanel";
import { CoreSymbolID, CoreSymbolNumberMap, getMultiplierPanelIndex } from "servertypes/SharedTypes";
import { MultiplierPanelsLayout } from "./MultiplierPanelsModel";
import { Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";

type IdlePanelData = {
    currentIndex: number,
    panels: MultiplierPanel[]
    playing: boolean,
    timer: number
}

class MultiplierPanelSignals {
    showSymbol = new Signal();
}

export class MultiplierPanels extends View {
    private readonly idleWait: number = this.services.resources.getSetting(`multiplierPanel`).idlePulse.wait;
    private panels: MultiplierPanel[] = [];
    private idlePanelData : IdlePanelData;
    signals = new MultiplierPanelSignals();

    constructor(protected override readonly services: GameServices) {
        super(services);

        this.idlePanelData = this.newIdlePanelData();
    }

    setup(layout: MultiplierPanelsLayout): void {

        this.services.factory.applySpec(this, {x: layout.x, y: layout.y});

        const {panelCount, panelSize, panelSpacing, potSize} = layout;

        let panel;

        for (let i=0; i<panelCount; i++) {

            panel = new MultiplierPanel(this.services, panelSize.width);
            panel.x = (i * panelSize.width) + (panelSize.width/2) + (i * panelSpacing) + (i > 2 ? potSize : 0);
            panel.signals.showSymbol.add(() => this.signals.showSymbol.dispatch());

            this.addChild(panel);
            this.panels.push(panel);
        }
    }

    setPanels(symbolMultiplierSet: CoreSymbolNumberMap, data?: Partial<CoreSymbolNumberMap>): void {

        if (data) {

            let symbolID, panelIndex;

            for (const [key, gamesRemaining] of Object.entries(data)) {

                symbolID = key as CoreSymbolID;
                panelIndex = getMultiplierPanelIndex(symbolID);

                this.panels[panelIndex].set(symbolID, symbolMultiplierSet[symbolID], gamesRemaining, false);
            }
        }
    }

    setPanel(panelIndex: number, symbol: CoreSymbolID, multiplier: number, counter: number, multiplierChange=false): void {
        this.panels[panelIndex].set(symbol, multiplier, counter, multiplierChange);
    }

    showPanel(panelIndex: number, symbol: CoreSymbolID, multiplier: number, counter: number, showSymbol=false, wheelPrizeCollection=false): void {
        this.panels[panelIndex].show(symbol, multiplier, counter, showSymbol, wheelPrizeCollection);
    }

    clearIdleAnim(): void {

        for (const panel of this.idlePanelData.panels) {
            panel.clearIdlePulse();
        }

        this.idlePanelData = this.newIdlePanelData();
    }

    showPanelSymbol(panelIndex: number): void {
        this.panels[panelIndex].showSymbol();
    }

    hidePanel(panelIndex: number): void {
        this.panels[panelIndex].hide();
    }

    resetPanel(panelIndex: number): void {
        this.panels[panelIndex].reset();
    }

    /**
     *
     * @param dt Seconds
    */
      tick(dt: number): void {

        if (!this.idlePanelData.playing) {

            const ms = dt * 1000;

            this.idlePanelData.timer += ms;

            if (this.idlePanelData.timer >= this.idleWait) {

                this.idlePanelData.timer = 0;
                this.idlePanelData.playing = true;
                this.startIdleAnim();
            }
        }
    }

    private newIdlePanelData(): IdlePanelData {
        return {
            currentIndex: 0,
            panels: [],
            playing: false,
            timer: 0
        };
    }

    private startIdleAnim(): void {

        this.idlePanelData.currentIndex = 0;
        this.idlePanelData.panels = this.panels.filter((value) => value.visible);

        if (this.idlePanelData.panels.length > 0) {
            this.nextIdlePanelAnim();
        }
    }

    private nextIdlePanelAnim(): void {

        if (this.idlePanelData.currentIndex < this.idlePanelData.panels.length) {

            const panel = this.idlePanelData.panels[this.idlePanelData.currentIndex];

            panel.signals.idlePulseComplete.once(this.nextIdlePanelAnim, this);
            panel.idlePulse();

            this.idlePanelData.currentIndex++;
        } else {
            this.idlePanelData.currentIndex = 0;
            this.idlePanelData.playing = false;
        }
    }

    override reset(): void {

        Tween.removeTweens(this);

        this.idlePanelData = this.newIdlePanelData();

        for (const panel of this.panels) {
            panel.reset();
        }
    }
}